import './platform.css';
import snakeEmpireThumbnailUrl from '../../games/snake-empire/docs/img_8.png?url';
import floraShieldUrl from '../../games/ghost-man/assets/flora.svg?url';
import legnarelloShieldUrl from '../../games/ghost-man/assets/legnarello.svg?url';
import sanbernardinoShieldUrl from '../../games/ghost-man/assets/sanbernardino.svg?url';
import sandomenicoShieldUrl from '../../games/ghost-man/assets/sandomenico.svg?url';
import sanmagnoShieldUrl from '../../games/ghost-man/assets/sanmagno.svg?url';
import sanmartinoShieldUrl from '../../games/ghost-man/assets/sanmartino.svg?url';
import santambrogioShieldUrl from '../../games/ghost-man/assets/santambrogio.svg?url';
import santerasmoShieldUrl from '../../games/ghost-man/assets/santerasmo.svg?url';
import nukeManPreviewUrl from '../../games/nuke-man/assets/preview.svg?url';
import trovaIntrusoPreviewUrl from '../../games/trova-intruso/assets/preview.svg?url';
import primordialePreviewUrl from '../../games/primordiale/assets/preview.svg?url';

const GHOST_MAN_SHIELDS = [
  sandomenicoShieldUrl,
  sanbernardinoShieldUrl,
  legnarelloShieldUrl,
  floraShieldUrl,
  sanmagnoShieldUrl,
  sanmartinoShieldUrl,
  santambrogioShieldUrl,
  santerasmoShieldUrl,
];

interface GameEntry {
  id: string;
  name: string;
  description: string;
  tags: string[];
  emoji: string;
  thumbnail: string | null;
  thumbnailGallery?: string[];
  url: string | null; // null = coming soon
  status: 'live' | 'soon';
  alpha?: boolean; // true = show alpha ribbon + TEST button
}

const GAMES: GameEntry[] = [
  {
    id: 'snake-empire',
    name: 'Snake Empire',
    description:
      'Competitive 2D arcade snake game. Grow your snake, collect orbs, crush opponents. Features Olympic mode, tournaments, and 15 bot nations.',
    tags: ['Action', 'Arcade', 'Multiplayer (bots)'],
    emoji: 'SN',
    thumbnail: snakeEmpireThumbnailUrl,
    url: '/games/snake-empire/',
    status: 'live',
    alpha: true,
  },
  {
    id: 'olympic-manager',
    name: 'Olympic Games Manager',
    description:
      'Gestisci la nazionale olimpica nella 100m piana. Scegli una delle 12 nazioni, segui il calendario, guarda le gare live e controlla il tuo atleta in tempo reale.',
    tags: ['Manager', 'Sport', 'Simulazione'],
    emoji: '🏅',
    thumbnail: '/thumbnails/olympic-manager.png',
    url: '/games/olympic-manager/',
    status: 'live',
    alpha: true,
  },
  {
    id: 'nuke-man',
    name: 'Nuke Man',
    description:
      'Stealth arcade tattico: posiziona NukeMan, lancia su bersagli strategici e sopravvivi alla caccia nemica fino all impatto.',
    tags: ['Action', 'Stealth', 'Tactical'],
    emoji: 'NM',
    thumbnail: nukeManPreviewUrl,
    url: '/games/nuke-man/',
    status: 'live',
  },
  {
    id: 'ghost-man',
    name: 'Caccia alla Croce',
    description:
      'Scegli il tuo fantasma di contrada e intercetta il Custode della Croce prima che completi i suoi obiettivi nel labirinto. A ogni livello devi qualificarti catturandolo: chi resta indietro viene eliminato dalla caccia.',
    tags: [],
    emoji: 'CC',
    thumbnail: null,
    thumbnailGallery: GHOST_MAN_SHIELDS,
    url: '/games/ghost-man/',
    status: 'live',
  },
  {
    id: 'primordiale',
    name: 'Primordiale',
    description:
      'Parti da una singola cellula e guida la tua specie attraverso 5 fasi evolutive. Sblocca tratti, riproduci esemplari, sopravvivi alle specie rivali. Real-time con pausa — ogni partita racconta una storia diversa.',
    tags: ['Strategia', 'Evoluzione', 'RTS'],
    emoji: '🦠',
    thumbnail: primordialePreviewUrl,
    url: '/games/primordiale/',
    status: 'live',
    alpha: true,
  },
  {
    id: 'trova-intruso',
    name: "Trova l'Intruso",
    description:
      'Puzzle visuale con 20 immagini generate a difficolta crescente. Trova il singolo elemento fuori posto osservando colori, forme, pattern, orientamento e piccoli dettagli.',
    tags: ['Puzzle', 'Visual', 'Canvas'],
    emoji: 'TI',
    thumbnail: trovaIntrusoPreviewUrl,
    url: '/games/trova-intruso/',
    status: 'live',
  },
];

function buildCard(game: GameEntry): HTMLElement {
  const card = document.createElement('div');
  card.className = `game-card${game.status === 'soon' ? ' coming-soon' : ''}`;

  // Thumbnail area — wrapped to contain the alpha ribbon
  const thumbWrap = document.createElement('div');
  thumbWrap.className = 'card-thumb-wrap';

  if (game.thumbnailGallery) {
    thumbWrap.appendChild(buildThumbGallery(game.thumbnailGallery));
  } else if (game.thumbnail) {
    const img = document.createElement('img');
    img.className = 'card-thumb';
    img.src = game.thumbnail;
    img.alt = game.name;
    img.onerror = () => {
      img.replaceWith(buildThumbPlaceholder(game.emoji));
    };
    thumbWrap.appendChild(img);
  } else {
    thumbWrap.appendChild(buildThumbPlaceholder(game.emoji));
  }

  if (game.alpha) {
    const ribbon = document.createElement('div');
    ribbon.className = 'alpha-ribbon';
    ribbon.textContent = 'ALPHA';
    thumbWrap.appendChild(ribbon);
  }

  card.appendChild(thumbWrap);

  const body = document.createElement('div');
  body.className = 'card-body';

  const name = document.createElement('div');
  name.className = 'card-name';
  name.textContent = game.name;

  const desc = document.createElement('div');
  desc.className = 'card-desc';
  desc.textContent = game.description;

  const tags = document.createElement('div');
  tags.className = 'card-tags';
  for (const t of game.tags) {
    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.textContent = t;
    tags.appendChild(tag);
  }

  const footer = document.createElement('div');
  footer.className = 'card-footer';

  const status = document.createElement('span');
  status.className = 'card-status';
  status.textContent = game.status === 'live' ? 'LIVE' : '';

  if (game.url) {
    const gameUrl = game.url;
    const btn = document.createElement('a');
    btn.className = game.alpha ? 'play-btn alpha-btn' : 'play-btn';
    btn.href = gameUrl;
    btn.textContent = game.alpha ? 'TEST' : 'PLAY';
    btn.addEventListener('click', (event) => launchGame(event, gameUrl));
    footer.appendChild(status);
    footer.appendChild(btn);
  } else {
    const coming = document.createElement('span');
    coming.className = 'coming-label';
    coming.textContent = 'COMING SOON';
    footer.appendChild(coming);
  }

  body.appendChild(name);
  body.appendChild(desc);
  if (game.tags.length > 0) {
    body.appendChild(tags);
  }
  body.appendChild(footer);
  card.appendChild(body);

  return card;
}

function launchGame(event: MouseEvent, url: string): void {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  event.preventDefault();
  void enterFullscreen().finally(() => {
    window.location.href = url;
  });
}

async function enterFullscreen(): Promise<void> {
  if (document.fullscreenElement || !document.documentElement.requestFullscreen) {
    return;
  }

  try {
    await document.documentElement.requestFullscreen();
  } catch {
    // Fullscreen is browser-controlled and can be rejected even after a click.
  }
}

function buildThumbPlaceholder(emoji: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'card-thumb-placeholder';
  el.textContent = emoji;
  return el;
}

function buildThumbGallery(images: string[]): HTMLElement {
  const gallery = document.createElement('div');
  gallery.className = 'card-thumb-shields';
  gallery.setAttribute('role', 'img');
  gallery.setAttribute('aria-label', 'Scudi delle 8 contrade di Legnano');

  for (const imageUrl of images) {
    const img = document.createElement('img');
    img.className = 'card-shield';
    img.src = imageUrl;
    img.alt = '';
    gallery.appendChild(img);
  }

  return gallery;
}

function init(): void {
  document.body.innerHTML = `
    <header class="platform-header">
      <div class="platform-logo">GAME<span>LAB</span></div>
      <div class="platform-tagline">BROWSER GAMES - NO INSTALL NEEDED</div>
    </header>
    <div class="platform-filters">
      <button class="filter-btn active">ALL</button>
      <button class="filter-btn">ACTION</button>
      <button class="filter-btn">ARCADE</button>
      <button class="filter-btn">STEALTH</button>
      <button class="filter-btn">MAZE</button>
      <button class="filter-btn">CONTRADE</button>
    </div>
    <main class="platform-grid" id="game-grid"></main>
    <section class="platform-contact" aria-labelledby="contact-title">
      <div>
        <h2 id="contact-title">Contatti</h2>
        <p>Per informazioni, idee o segnalazioni scrivi a:</p>
      </div>
      <a href="mailto:lostivalediacciaio@gmail.com">lostivalediacciaio@gmail.com</a>
    </section>
  `;

  const grid = document.getElementById('game-grid')!;
  for (const game of GAMES) {
    grid.appendChild(buildCard(game));
  }
}

init();
